/*******************************************************************************
 NAME     : createEngineSlice
 PURPOSE  : FE-01 — zustand slice caching the capability inventory per ENGINE.

 One inventory per engine, not per project: GET /project/engine-capabilities
 takes no parameters and publishes every provider at once
 (FE_INTEGRATION_API.md §3), so every project on the same engine shares one
 answer. The `__default` bucket holds the read made before any engine is known.

 REVISIONS:
 Ver.       Date        Author           Description
 ---------  ----------  ---------------  ------------------------------------
 1.0        2026-08-13  HieuNT161        1. FE-01 최초 작성
 *******************************************************************************/
import { createFallbackInventory } from "@maxis/client-logic/apis/engineCapability";

/** Bucket key used before the engine of the row is known. */
export const DEFAULT_ENGINE_SCOPE = "__default";

export const engineScopeKey = (engineCode) =>
  engineCode === undefined || engineCode === null || engineCode === ""
    ? DEFAULT_ENGINE_SCOPE
    : String(engineCode);

export const createEngineSlice = (set, get) => ({
  /** @type {Object.<string, Object>} normalized inventory keyed by engine */
  inventoryByScope: {},
  /** @type {Object.<string, boolean>} in-flight flag keyed by scope */
  loadingByScope: {},

  setInventory: (engineCode, inventory) => {
    const key = engineScopeKey(engineCode);
    set((state) => {
      state.engine.inventoryByScope[key] = inventory;
      state.engine.loadingByScope[key] = false;
    });
  },

  setInventoryLoading: (engineCode, loading) => {
    const key = engineScopeKey(engineCode);
    set((state) => {
      state.engine.loadingByScope[key] = Boolean(loading);
    });
  },

  getInventory: (engineCode) => {
    const key = engineScopeKey(engineCode);
    return get().engine.inventoryByScope[key] ?? createFallbackInventory();
  },

  isInventoryLoaded: (engineCode) => {
    const key = engineScopeKey(engineCode);
    return Object.prototype.hasOwnProperty.call(
      get().engine.inventoryByScope,
      key
    );
  },

  /** Drop one engine's entry (forced refresh) or all of them. */
  resetEngine: (engineCode) => {
    set((state) => {
      if (engineCode === undefined) {
        state.engine.inventoryByScope = {};
        state.engine.loadingByScope = {};
        return;
      }
      const key = engineScopeKey(engineCode);
      delete state.engine.inventoryByScope[key];
      delete state.engine.loadingByScope[key];
    });
  },
});
